import * as React from 'react'
import { format } from 'date-fns'
import { BarChart3, TrendingUp, FileText, Search, Calendar, MapPin } from 'lucide-react'
import { getOrders } from '@/api/orders'
import type { Order } from '@/api/orders'
import { Button } from '@/components/ui/button'

const fmtDateTime = (iso: string) => {
  if (!iso) return '-'
  const d = iso.includes('T') || iso.endsWith('Z') ? new Date(iso) : new Date(iso + 'Z')
  return format(d, 'MM/dd HH:mm')
}

const defaultDateFrom = () => {
  const d = new Date()
  d.setMonth(d.getMonth() - 2)
  return format(d, 'yyyy-MM-dd')
}
const defaultDateTo = () => format(new Date(), 'yyyy-MM-dd')

export default function DriverMyReportPage() {
  const [orders, setOrders] = React.useState<Order[]>([])
  const [loading, setLoading] = React.useState(true)
  const [dateFrom, setDateFrom] = React.useState(defaultDateFrom())
  const [dateTo, setDateTo] = React.useState(defaultDateTo())
  const [locationKeyword, setLocationKeyword] = React.useState('')

  const fetchOrders = async (from: string, to: string) => {
    setLoading(true)
    try {
      const res = await getOrders({
        limit: 1000,
        date_from: from,
        date_to: to,
      })
      const sorted = [...res.data].sort((a, b) => new Date(b.pickup_datetime).getTime() - new Date(a.pickup_datetime).getTime())
      setOrders(sorted)
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => { fetchOrders(dateFrom, dateTo) }, [])

  const filteredOrders = orders.filter(o => {
    if (!locationKeyword.trim()) return true
    const kw = locationKeyword.trim()
    return (o.pickup_location || '').includes(kw) || (o.dropoff_location || '').includes(kw)
  })

  const completedOrders = filteredOrders.filter(o => o.status === '已完成')
  const totalDriverFee = Math.round(completedOrders.reduce((sum, o) => sum + (o.pricing?.driver_fee || 0), 0))
  const totalRebate = Math.round(completedOrders.reduce((sum, o) => sum + ((o.pricing?.vendor_rebate || 0) + (o.pricing?.vendor_rebate_2 || 0)), 0))

  return (
    <div className="space-y-4 pb-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 dark:bg-blue-900/30">
          <BarChart3 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">我的報表</h1>
      </div>

      {/* 篩選條件 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />開始日期</label>
            <input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><Calendar className="h-3 w-3" />結束日期</label>
            <input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <div className="flex-1 min-w-[160px] space-y-1">
            <label className="text-xs font-medium text-gray-500 flex items-center gap-1"><MapPin className="h-3 w-3" />地點關鍵字</label>
            <input
              type="text"
              value={locationKeyword}
              onChange={e => setLocationKeyword(e.target.value)}
              placeholder="搜尋上車/下車地址"
              className="flex h-10 w-full rounded-md border border-input bg-white dark:bg-gray-800 px-3 text-sm shadow-sm outline-none"
            />
          </div>
          <Button
            onClick={() => fetchOrders(dateFrom, dateTo)}
            disabled={loading}
            className="h-10 px-6 bg-blue-600 hover:bg-blue-700 text-white font-bold"
          >
            <Search className="h-4 w-4 mr-2" />
            {loading ? '查詢中...' : '查詢'}
          </Button>
        </div>
      </div>

      {/* 統計卡片 */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><FileText className="h-4 w-4" />近2個月訂單</div>
          <div className="text-2xl font-black text-gray-900 dark:text-white">{filteredOrders.length} 筆</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><TrendingUp className="h-4 w-4" />司機費用合計</div>
          <div className="text-2xl font-black text-blue-600 dark:text-blue-400">${totalDriverFee.toLocaleString()}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
          <div className="flex items-center gap-2 text-gray-500 text-xs mb-1"><TrendingUp className="h-4 w-4" />回金合計</div>
          <div className="text-2xl font-black text-orange-500">${totalRebate.toLocaleString()}</div>
        </div>
      </div>

      {/* 歷史紀錄 */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <span className="font-bold text-gray-900 dark:text-white text-sm">承接紀錄</span>
        </div>
        {loading ? (
          <div className="py-16 text-center text-gray-400">載入中...</div>
        ) : filteredOrders.length === 0 ? (
          <div className="py-16 text-center text-gray-400">此條件無承接紀錄</div>
        ) : (
          <>
            {/* 手機：卡片列表 */}
            <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
              {filteredOrders.map(order => (
                <div key={order.id} className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{fmtDateTime(order.pickup_datetime)}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{order.payment_method}</span>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                    <p>上車：{order.pickup_location}</p>
                    <p>下車：{order.dropoff_location}</p>
                  </div>
                  <div className="flex items-center gap-4 text-xs pt-1">
                    <span className="text-gray-600 dark:text-gray-400">司機費用：<span className="font-bold text-blue-600 dark:text-blue-400">${Math.round(order.pricing?.driver_fee || 0).toLocaleString()}</span></span>
                    <span className="text-gray-600 dark:text-gray-400">回金：<span className="font-bold text-orange-500">${Math.round((order.pricing?.vendor_rebate || 0) + (order.pricing?.vendor_rebate_2 || 0)).toLocaleString()}</span></span>
                  </div>
                </div>
              ))}
            </div>

            {/* 桌機：表格 */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">日期時間</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">付款方式</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">上車地址</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">下車地址</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">司機費用</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">回金</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {filteredOrders.map((order, idx) => (
                    <tr key={order.id} className={idx % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-slate-50 dark:bg-gray-750'}>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtDateTime(order.pickup_datetime)}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{order.payment_method}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-[200px] truncate">{order.pickup_location}</td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 max-w-[200px] truncate">{order.dropoff_location}</td>
                      <td className="px-4 py-3 text-right font-bold text-blue-600 dark:text-blue-400">${Math.round(order.pricing?.driver_fee || 0).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right font-bold text-orange-500">${Math.round((order.pricing?.vendor_rebate || 0) + (order.pricing?.vendor_rebate_2 || 0)).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
